import uniqueId from "lodash.uniqueid";
import cx from "classnames";
import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";

import { loadScript, createPlayer } from "./youtubeService.js";
import scTracking from "../SCTracking/sc-tracking.js";
import { isScrolledIntoView } from "../../common/scrollIntoView.js";

import ArrowLink from "../ArrowLink/ArrowLink.jsx";
import Icon from "../Icon/Icon.jsx";
import ImageElement from "../ImageElement/ImageElement.jsx";
import VideoButton from "../VideoButton/VideoButton.jsx";

import { object, string, bool, shape } from "prop-types";

export const propTypes = {
  id: string.isRequired,
  params: string,
  image: shape({
    srcSet: string,
  }),
  transcriptLink: object,
  title: string,
  caption: string,
  credit: string,
  darkButton: bool,
  smallButton: bool,
  autoplay: bool,
  language: shape({
    Name: string,
  }),
};

const YouTubeVideo = (props) => {
  const {
    id,
    params,
    image,
    transcriptLink,
    title,
    caption,
    credit,
    darkButton,
    smallButton,
    autoplay,
    language,
  } = props;

  const [videoStarted, setVideoStarted] = useState(autoplay);
  const [player, setPlayer] = useState(null);
  const [playerReady, setPlayerReady] = useState(false);
  const rootRef = useRef(null);

  const rootId = useMemo(() => uniqueId(), []);
  const playerId = useMemo(() => uniqueId(), []);

  const init = useCallback(() => {
    const config = {
      videoId: id,
      playerVars: {
        autoplay: autoplay ? 1 : 0,
        cc_lang_pref: language?.Name || "en",
        hl: language?.Name || "en",
      },
    };

    const paramsToObject = (params) => {
      const entries = new URLSearchParams(params);

      const result = {};
      for (const [key, value] of entries) {
        // each 'entry' is a [key, value] tupple
        result[key] = value;
      }
      return result;
    };

    if (params && params !== "") {
      config.playerVars = { ...config.playerVars, ...paramsToObject(params) };
    }

    const onPlayerReady = (e) => {
      if (autoplay) {
        e.target.mute();
      }
      setPlayerReady(true);
    };

    function handleTextOverMediaModalOpen(e) {
      if (rootId === e.detail.id) playVideo();
    }

    function initVideo() {
      const newPlayer = createPlayer(playerId, config, onPlayerReady);
      setPlayer(newPlayer);

      window.addEventListener(
        "text-over-media-modal-open",
        handleTextOverMediaModalOpen,
      );
    }
    // Check if window.onYouTubeIframeAPIReady already exists
    if (window.YT && window.YT.loaded) {
      initVideo();
    } else {
      // after onYouTubeIframeAPIReady is executed, create a new player
      const callback = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        if (callback) {
          callback();
        }

        initVideo();
      };
    }

    return () => {
      window.removeEventListener(
        "text-over-media-modal-open",
        handleTextOverMediaModalOpen,
      );
    };
  }, [id, autoplay, language, params, playerId]);

  useEffect(() => {
    // On mount, check to see if the API script is already loaded
    loadScript();

    const root = rootRef.current;

    // Lazy load initialization of the YouTube player
    const observer = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          init();
          observer.unobserve(root);
        }
      });
    });

    if (root && isScrolledIntoView(root)) {
      init();
    } else {
      observer.observe(root);
    }

    return () => {
      observer.unobserve(root);
    };
  }, [init]);

  useEffect(() => {
    // If video's started but player is not ready, play video when ready
    if (videoStarted && playerReady) {
      player.playVideo();
    }
  }, [player, playerReady, videoStarted]);

  const playVideo = () => {
    setVideoStarted(true);
    scTracking("videoplay");

    if (playerReady) {
      player.playVideo();
    }
  };

  useEffect(() => {
    if (!player || !rootRef.current) return;

    const root = rootRef.current;

    const pausedByObserver = new WeakMap();

    const observer = new IntersectionObserver(
      /**
       * If video is playing when it leaves the viewport, pause it.
       * If video then re-enters the viewport, play it.
       */
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Video is entering the viewport - play it if it was paused
            // by the observer.
            if (pausedByObserver.get(root)) {
              player.playVideo();
              pausedByObserver.set(root, false);
            }
          } else {
            // Video is leaving the viewport - pause it and track that it was
            // paused by the observer.
            // https://developers.google.com/youtube/iframe_api_reference#Playback_status
            if (
              player &&
              player.getPlayerState &&
              player.getPlayerState() === window.YT.PlayerState.PLAYING
            ) {
              player.pauseVideo();
              pausedByObserver.set(root, true);
            }
          }
        });
      },
      { threshold: 0.5 },
    );

    observer.observe(root);

    return () => {
      observer.unobserve(root);
    };
  }, [player]);

  return (
    <div
      className={cx("youtube-video", {
        "youtube-video--video-started": videoStarted,
      })}
      id={rootId}
      ref={rootRef}
    >
      <figure className="youtube-video__media">
        <div className="youtube-video__video-wrapper">
          <VideoButton
            isPlaying={videoStarted}
            darkTheme={darkButton}
            smallButton={smallButton}
            onClick={playVideo}
            label="play video"
            className="youtube-video__poster-button"
          >
            {image && image.srcSet ? (
              <ImageElement
                className="youtube-video__poster-image"
                {...image}
              />
            ) : (
              <img
                className="youtube-video__poster-image"
                alt=""
                src={`https://i3.ytimg.com/vi/${id}/maxresdefault.jpg`}
              />
            )}
          </VideoButton>
          <div id={playerId} />
        </div>
        <div className="youtube-video__caption">
          {(title || caption || credit) && (
            <figcaption className="youtube-video__figure-caption">
              {title && (
                <h2 className="youtube-video__title txt-heading-b">{title}</h2>
              )}
              {caption && (
                <span className="youtube-video__description">{caption}</span>
              )}
              {credit && (
                <span className="youtube-video__credit">{credit}</span>
              )}
            </figcaption>
          )}
          {transcriptLink && (
            <div className="youtube-video__transcript">
              <ArrowLink {...transcriptLink} />
            </div>
          )}
        </div>
      </figure>
    </div>
  );
};

YouTubeVideo.propTypes = propTypes;

export default YouTubeVideo;
